import * as React from "react";
import { useState } from "react";
import { useStyles } from "../../style/Pages/BasicCompanyInfo";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import { Button, Grid, Typography, MenuItem, IconButton, CircularProgress } from "@mui/material";
import { schemaValidationLegalCompanyInfo, schemaValidationLegalCompany } from "../../utils/validation";
import { InputField } from "../../components/InputController";
import { useDispatch, useSelector } from "react-redux";
import { clearOnboardingApplicationFlags, getPmDetails, getUpdateOnboardingApplication, selectOnboardingApplicationState } from "./sagas/pmonboardingSlice";
import { useHistory } from "react-router-dom";
import CustomDropdown from "../../components/customDropdown";
import { approvedISOStandards } from "../../shared/util/approvedISOStandards";
import { removeDashes } from "../shared/util/helpers";
import { CompanyType } from "@inosio/payments-sdk";
import BackIcon from "../../icons/backIcon.svg";

interface IState {
  name: CompanyType;
}

export const LegalInfo = () => {
  const classes = useStyles();
  const [companies] = useState<Array<IState>>([{ name: CompanyType.SoleProprietorship }, { name: CompanyType.Corporation }, { name: CompanyType.Partnership }, { name: CompanyType.Nonprofit }, { name: CompanyType.Llc }]);
  const history = useHistory();
  const dispatch = useDispatch();

  const { pmDetails, onboarding, isFetching, isSuccess } = useSelector(selectOnboardingApplicationState);

  //Legal Company Info Saved Values
  const [savedValues, setSavedValues] = React.useState<schemaValidationLegalCompanyInfo>({
    company_legal_name: "",
    company_DBA_name: "",
    company_mailing_address: "",
    city: "",
    state: approvedISOStandards.States[0].Code, // defaults to Alabama
    zip: "",
    country: "US", // defaults to US
    state_of_incorporation: approvedISOStandards.States[0].Code, // defaults to Alabama
    tax_ID: "",
    company_type: "",
  });

  React.useEffect(() => {
    onboarding?.DbaName &&
      setSavedValues({
        ...savedValues,
        company_legal_name: onboarding?.LegalName!,
        company_DBA_name: onboarding?.DbaName!,
        company_mailing_address: onboarding?.MailingAddress?.Line1!,
        city: onboarding?.MailingAddress?.City!,
        state: onboarding?.MailingAddress?.State!,
        zip: onboarding?.MailingAddress?.Zip!,
        country: onboarding?.MailingAddress?.Country!,
        state_of_incorporation: onboarding?.IncorporationState!,
        tax_ID: onboarding?.TaxId!,
        company_type: onboarding?.CompanyType!,
      });
  }, [onboarding]);

  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);
  
  const formik = useFormik<schemaValidationLegalCompanyInfo>({
    initialValues: savedValues,
    validationSchema: schemaValidationLegalCompany,
    onSubmit: async (values) => {
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: onboarding?.Uuid || pmDetails?.Company?.Uuid,
          data: {
            IncorporationState: values.state_of_incorporation,
            TaxId: removeDashes(values.tax_ID),
            CompanyType: values.company_type,
            MailingAddress: {
              City: values.city,
              State: values.state,
              Zip: values.zip,
              Line1: values.company_mailing_address,
              Country: values.country,
            },
            DbaName: values.company_DBA_name,
            LegalName: values.company_legal_name,
          },
        },
      });
      setIsFormSubmitted(true);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  React.useEffect(() => {
    if (isSuccess) {
      if (isFormSubmitted) history.push("/onboarding/applicationwizard");
      
      // Clear all onboarding flags
      dispatch({
        type: clearOnboardingApplicationFlags.type,
        payload: {},
      });
    }
  }, [isFormSubmitted, isSuccess]);

  React.useEffect(() => {
    // Clear all onboarding flags
    dispatch({
      type: clearOnboardingApplicationFlags.type,
      payload: {},
    });

    // fetch company uuid if onboarding state has been erased, probably due to a refresh
    !onboarding?.Uuid &&
      dispatch({
        type: getPmDetails.type,
        payload: {},
      });
  }, []);

  React.useEffect(() => {
    // fetch onboarding application if it doesnt exist in app context
    pmDetails.Company?.Uuid &&
      !onboarding?.Uuid &&
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: pmDetails.Company.Uuid,
          data: {},
        },
      });
  }, [pmDetails]);

  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;
  const [selectedCountry, setSelectedCountry] = React.useState("US");
  const [selectedState, setSelectedState] = React.useState(approvedStates[0].Code);
  const resetStateSelection = () => {
    formik.setFieldValue("state", "");
    formik.setFieldValue("state_of_incorporation", "");
    setSelectedState("");
  };

  // load selected state if a valid selection was made originally, else let plain text box render state of origin
  React.useEffect(() => {
    formik.values.country && ["US", "CA"].includes(formik.values.country) && approvedStates.map((stateRecord) => stateRecord.Code).includes(formik.values.state) && setSelectedState(formik.values.state);
  }, [onboarding, formik]);

  const handleClickFinishLater = () => {
    history.push("/onboarding/applicationwizard");
  };

  return (
    <Grid item xs={11} sm={9} lg={8} xl={7}>
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
        <IconButton
          sx={{ p: 1 }}
          onClick={handleClickFinishLater}
        >
          <img src={BackIcon} alt="cancel icon" width="17px" height="17px" />
        </IconButton>
        <Typography
          style={{
            fontWeight: 600,
            fontSize: "18px",
          }}
          sx={{ p: 1 }}
          className={classes.companyInfo}
        >
          Legal Company Info
        </Typography>
      </Box>

      <Box component="form" noValidate onSubmit={formik.handleSubmit} className={classes.centerLabel}>
        <InputField
          name={"company_legal_name"}
          placeholder={"Company Legal Name"}
          label={"Company Legal Name"}
          isError={false}
          errors={formik.errors?.company_legal_name}
          touched={formik.touched?.company_legal_name}
          onBlur={formik.handleBlur}
          onChange={(e) => formik.setFieldValue("company_legal_name", e.target.value)}
          value={formik.values.company_legal_name || ""}
        />
        <InputField
          name={"company_DBA_name"}
          placeholder={"Company DBA Name"}
          label={"Company DBA Name"}
          isError={false}
          errors={formik.errors?.company_DBA_name}
          touched={formik.touched?.company_DBA_name}
          onBlur={formik.handleBlur}
          onChange={(e) => formik.setFieldValue("company_DBA_name", e.target.value)}
          value={formik.values.company_DBA_name || ""}
        />

        <InputField
          name={"company_mailing_address"}
          placeholder={"Company Mailing Address"}
          label={"Company Mailing Address"}
          isError={false}
          errors={formik.errors?.company_mailing_address}
          touched={formik.touched?.company_mailing_address}
          onBlur={formik.handleBlur}
          onChange={(e) => formik.setFieldValue("company_mailing_address", e.target.value)}
          value={formik.values.company_mailing_address || ""}
        />
        <Grid container spacing={1} direction="row">
          <Grid item xs={12} sm={6}>
            <InputField
              name={"city"}
              placeholder={"City"}
              label={"City"}
              isError={false}
              errors={formik.errors?.city}
              touched={formik.touched?.city}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("city", e.target.value)}
              value={formik.values.city || ""}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {selectedCountry && ["US", "CA"].includes(selectedCountry) ? (
              <>
                <CustomDropdown
                  isFullWidth
                  label={"State"}
                  value={selectedState}
                  handleChange={(e: any) => {
                    setSelectedState(e.target.value);
                    formik.setFieldValue("state", e.target.value);
                  }}
                >
                  {selectedCountry === "US"
                    ? approvedStates.map((stateRecord) => (
                        <MenuItem style={{ height: "36px" }} key={stateRecord.Code} value={stateRecord.Code}>
                          {stateRecord.State + " - " + stateRecord.Code}
                        </MenuItem>
                      ))
                    : approvedProvinces.map((provinceRecord) => (
                        <MenuItem style={{ height: "36px" }} key={provinceRecord.Code} value={provinceRecord.Code}>
                          {provinceRecord.Province + " - " + provinceRecord.Code}
                        </MenuItem>
                      ))}{" "}
                </CustomDropdown>
              </>
            ) : (
              <InputField
                name={"state"}
                isError={false}
                errors={formik.errors?.state}
                touched={formik.touched?.state}
                onBlur={formik.handleBlur}
                onChange={(e: any) => formik.setFieldValue("state", e.target.value)}
                placeholder={"State"}
                value={formik.values.state}
                label={"State"}
              />
            )}
          </Grid>
        </Grid>
        <Grid container spacing={1} direction="row">
          <Grid item xs={12} sm={6}>
            <InputField
              name={"zip"}
              placeholder={"zip"}
              label={"zip"}
              isError={false}
              errors={formik.errors?.zip}
              touched={formik.touched?.zip}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("zip", e.target.value)}
              value={formik.values.zip || ""}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomDropdown
              label={"Country"}
              isFullWidth
              handleChange={(e: any) => {
                resetStateSelection();

                // update selected country
                setSelectedCountry(e?.target?.value);
                formik.setFieldValue("country", e?.target?.value);
              }}
              value={selectedCountry}
            >
              {approvedCountries.map((countryRecord) => (
                <MenuItem style={{ height: "36px" }} key={countryRecord.Code} value={countryRecord.Code}>
                  {countryRecord.Country + " - " + countryRecord.Code}
                </MenuItem>
              ))}
            </CustomDropdown>
          </Grid>
        </Grid>
        <CustomDropdown isFullWidth label={"State of Incorporation"} value={formik.values.state_of_incorporation} handleChange={(e: any) => formik.setFieldValue("state_of_incorporation", e?.target?.value)}>
          {selectedCountry === "US" ? (
            approvedStates.map((stateRecord, index) => (
              <MenuItem style={{ height: "36px" }} key={index} value={stateRecord.Code}>
                {stateRecord.State + " - " + stateRecord.Code}
              </MenuItem>
            ))
          ) : (
            <MenuItem style={{ height: "36px" }} value="NA">
              other
            </MenuItem>
          )}{" "}
        </CustomDropdown>
        <InputField
          name={"tax_ID"}
          placeholder={"EIN/Tax ID"}
          type="tax_ID"
          label={"EIN/Tax Id"}
          isError={false}
          errors={formik.errors?.tax_ID}
          touched={formik.touched?.tax_ID}
          onBlur={formik.handleBlur}
          onChange={(e) => formik.setFieldValue("tax_ID", e.target.value)}
          value={formik.values.tax_ID || ""}
        />
        <CustomDropdown label={"Company Type"} isFullWidth handleChange={(e) => formik.setFieldValue("company_type", e.target.value)} value={formik.values.company_type}>
          {companies.map((companyType, index) => (
            <MenuItem style={{ height: "36px" }} key={index} value={companyType.name}>
              {companyType.name}
            </MenuItem>
          ))}
        </CustomDropdown>
        <Box style={{ width: "100%" }} sx={{ display: "flex", justifyContent: "end" }}>
          <div className={classes.wrapper}>
            <Button type="submit" disabled={Boolean(!formik.isValid)} className={classes.signUpBtn}>
              <Typography className={classes.signUpBtnText} sx={{ margin: "0 10px" }}>Save</Typography>
            </Button>
            {isFormSubmitted && isFetching && <CircularProgress size={26} className={classes.buttonProgress} />}
          </div>
        </Box>
      </Box>
    </Grid>
  );
};
